import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Search, Ticket, Check } from "lucide-react";

export const Route = createFileRoute("/harikaradmin/grant-ticket")({
  head: () => ({ meta: [{ title: "Bilet Ver — JUËN Admin" }] }),
  component: AdminGrantTicketPage,
});

interface ProfileRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
}
interface EventRow {
  id: string;
  name: string;
  starts_at: string;
  status: string;
}
interface TicketTypeRow {
  id: string;
  name: string;
  price_kurus: number;
  event_id: string;
}

function AdminGrantTicketPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState<EventRow[]>([]);
  const [eventId, setEventId] = useState("");
  const [ticketTypes, setTicketTypes] = useState<TicketTypeRow[]>([]);
  const [ticketTypeId, setTicketTypeId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");

  const [memberQuery, setMemberQuery] = useState("");
  const [results, setResults] = useState<ProfileRow[]>([]);
  const [selected, setSelected] = useState<ProfileRow | null>(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      const { data } = await supabase
        .from("events")
        .select("id,name,starts_at,status")
        .order("starts_at", { ascending: false })
        .limit(100);
      setEvents((data as EventRow[]) ?? []);
    })();
  }, [isAdmin]);

  useEffect(() => {
    if (!eventId) {
      setTicketTypes([]);
      setTicketTypeId("");
      return;
    }
    void (async () => {
      const { data } = await supabase
        .from("ticket_types")
        .select("id,name,price_kurus,event_id")
        .eq("event_id", eventId)
        .order("sort_order", { ascending: true });
      const rows = (data as TicketTypeRow[]) ?? [];
      setTicketTypes(rows);
      setTicketTypeId(rows[0]?.id ?? "");
    })();
  }, [eventId]);

  useEffect(() => {
    const q = memberQuery.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      const like = `%${q}%`;
      const { data } = await supabase
        .from("profiles")
        .select("id,first_name,last_name,email,phone")
        .or(`email.ilike.${like},first_name.ilike.${like},last_name.ilike.${like},phone.ilike.${like}`)
        .limit(10);
      setResults((data as ProfileRow[]) ?? []);
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [memberQuery]);

  const canSubmit = useMemo(
    () => !!selected && !!eventId && !!ticketTypeId && quantity >= 1 && quantity <= 20,
    [selected, eventId, ticketTypeId, quantity],
  );

  const submit = async () => {
    if (!canSubmit || !selected) return;
    if (
      !confirm(
        `${selected.first_name} ${selected.last_name} için ${quantity} adet bilet oluşturulacak. Onaylıyor musun?`,
      )
    )
      return;
    setSubmitting(true);
    const { error } = await supabase.rpc("admin_grant_tickets", {
      _user_id: selected.id,
      _event_id: eventId,
      _ticket_type_id: ticketTypeId,
      _quantity: quantity,
      _notes: notes.trim() || undefined,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`${quantity} bilet oluşturuldu.`);
    setSelected(null);
    setMemberQuery("");
    setNotes("");
    setQuantity(1);
  };

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Bilet Ver</h1>
          <p className="mt-2 text-muted-foreground">
            Seçtiğin üyeye doğrudan bilet oluştur. Ödeme alınmaz, etkinlik limiti uygulanmaz.
          </p>
        </div>

        <AdminTabs />

        <div className="space-y-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <div>
            <Label>Etkinlik</Label>
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Seç...</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name} — {new Date(e.starts_at).toLocaleDateString("tr-TR")} ({e.status})
                </option>
              ))}
            </select>
          </div>

          {ticketTypes.length > 0 && (
            <div>
              <Label>Bilet türü</Label>
              <select
                value={ticketTypeId}
                onChange={(e) => setTicketTypeId(e.target.value)}
                className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {ticketTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({(t.price_kurus / 100).toLocaleString("tr-TR")} ₺)
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <Label>Adet</Label>
            <Input
              type="number"
              min={1}
              max={20}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
              className="mt-1.5 w-32"
            />
          </div>

          <div>
            <Label>Üye ara (e-posta, ad, telefon)</Label>
            <div className="relative mt-1.5">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={memberQuery}
                onChange={(e) => {
                  setMemberQuery(e.target.value);
                  setSelected(null);
                }}
                placeholder="alican@..."
                className="pl-9"
              />
            </div>
            {selected ? (
              <div className="mt-2 flex items-center justify-between rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm">
                <div>
                  <div className="font-medium">
                    {selected.first_name} {selected.last_name}
                  </div>
                  <div className="text-muted-foreground">{selected.email}</div>
                </div>
                <Check className="h-5 w-5 text-primary" />
              </div>
            ) : memberQuery.trim().length >= 2 ? (
              <div className="mt-2 max-h-60 overflow-auto rounded-lg border border-border">
                {searching ? (
                  <div className="p-3 text-sm text-muted-foreground">Aranıyor...</div>
                ) : results.length === 0 ? (
                  <div className="p-3 text-sm text-muted-foreground">Sonuç yok.</div>
                ) : (
                  results.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setSelected(r)}
                      className="flex w-full items-center justify-between border-b border-border px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted"
                    >
                      <span>
                        <span className="font-medium">
                          {r.first_name} {r.last_name}
                        </span>
                        <span className="ml-2 text-muted-foreground">{r.email}</span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>

          <div>
            <Label>Not (opsiyonel)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ör. Davet, telafi, basın..."
              rows={2}
              className="mt-1.5"
            />
          </div>

          <Button onClick={submit} disabled={!canSubmit || submitting} className="w-full">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Ticket className="mr-2 h-4 w-4" />}
            Bilet oluştur
          </Button>
        </div>
      </div>
    </div>
  );
}
